<template>
    <b-form-row v-if="patient">
        <b-col>
            <b-form-row cols-lg="2" cols="1" class="align-items-center">
                <b-col>
                    <SearchComponent v-model="selectedDiagnosis" label="Diagnosis Search" search-u-r-l="/Diagnosis"
                        :disabled="disable" clearable :results-modifier="formatResults" />
                </b-col>
                <b-col>
                    <label>Primary for Programs:</label>
                    <Multiselect v-model="selectedPrimaryPrograms" :options="programs" track-by="id"
                        label="name" multiple :disabled="disable" />
                </b-col>
            </b-form-row>

            <b-button @click="addDiagnosis" v-if="!disable" variant="success">Add Diagnosis</b-button>

            <b-table-simple v-show="patientDiagnosis.length > 0">
                <b-thead>
                    <b-tr>
                        <b-th colspan="2">Diagnosis</b-th>
                        <b-th class="primary-programs">Primary for Programs</b-th>
                        <b-th>Added Date</b-th>
                        <b-th class="remove"></b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-for="diagnosis in patientDiagnosis" :key="diagnosis.icD10Code">
                        <b-td>{{ diagnosis.icD10Code }}</b-td>
                        <b-td>{{ diagnosis.icD10Description }}</b-td>
                        <b-td>
                            <Multiselect v-model="diagnosis.primaryPrograms" :options="programs" track-by="id"
                                label="name" multiple :disabled="disable" @input="updateDiagnosis(diagnosis)" />
                        </b-td>
                        <b-td>
                            {{ diagnosis.created | formatDate("LLL", !!diagnosis.isUtc) }}
                        </b-td>
                        <b-td>
                            <b-button v-if="!disable" variant="danger" @click="removeDiagnosis(diagnosis)" size="sm">
                                Remove
                            </b-button>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>

            <div v-if="patient && patient.id && requireRemovalReason" class="mt-2">
                <b-button variant="link" size="sm" class="px-0" @click="toggleArchived">
                    {{ showArchived ? 'Hide' : 'Show' }} removed conditions ({{ archivedDiagnosis.length }})
                </b-button>
                <b-table-simple v-if="showArchived && archivedDiagnosis.length > 0" small>
                    <b-thead>
                        <b-tr>
                            <b-th colspan="2">Diagnosis</b-th>
                            <b-th>Removed Date</b-th>
                            <b-th>Removed By</b-th>
                            <b-th>Reason</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="archived in archivedDiagnosis" :key="`archived-${archived.id}`">
                            <b-td>{{ archived.icD10Code }}</b-td>
                            <b-td>{{ archived.icD10Description }}</b-td>
                            <b-td>{{ archived.removed | formatDate("LLL", !!archived.isUtc) }}</b-td>
                            <b-td>{{ archived.removedBy }}</b-td>
                            <b-td>{{ archived.removalReason }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
                <div v-else-if="showArchived" class="text-muted small">No removed conditions.</div>
            </div>

            <RemovalReasonModal ref="reasonModal" />
        </b-col>
    </b-form-row>
</template>

<script lang="ts">
import { Component, Watch, Prop, Vue, Ref } from 'vue-property-decorator';
import { DatePicker } from "element-ui";
import axios from 'axios';
import { Patient } from '@/models/Patient';

import SearchComponent from '@/components/SearchComponent.vue';
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import AddressFormFields from '@/components/AddressFormFields.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import RemovalReasonModal from '@/components/Patient/RemovalReasonModal.vue';
import { GSDDPatientDiagnosis, ICD10Term } from "@/models/GSDD";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { dedupeIcd10Results } from "@/util/icd10Results";
import Multiselect from 'vue-multiselect';
import { Program } from '@/models/Program';

@Component({
    name: "GSDDDiagnosisComponent",
    components: {
        SearchComponent,
        NewClearSaveButtons,
        AddressFormFields,
        Checkbox,
        RemovalReasonModal,
        [DatePicker.name]: DatePicker,
        Multiselect
    }
})

export default class GSDDDiagnosisComponent extends Vue {
    @Prop({ default: null }) private patient!: Patient;
    @Prop({ default: false }) private disable!: boolean;
    @Prop({ default: () => [] }) private value!: GSDDPatientDiagnosis[];

    @Ref('reasonModal') private reasonModal!: RemovalReasonModal;

    private selectedDiagnosis: ICD10Term = new ICD10Term();
    private selectedPrimaryPrograms: Program[] = [];

    protected programs: Program[] = [];
    protected requireRemovalReason = false;
    protected showArchived = false;
    protected archivedDiagnosis: GSDDPatientDiagnosis[] = [];
    // Archived list is lazy-loaded on first expand and cached; invalidated when a removal changes it.
    private archivedLoaded = false;

    get patientDiagnosis(): GSDDPatientDiagnosis[] {
        return this.value;
    }

    set patientDiagnosis(val: GSDDPatientDiagnosis[]) {
        this.$emit('input', val);
    }

    created() {
        this.getPatientGSDDDiagnosis();
        this.fetchPrograms();
        this.fetchRemovalReasonConfig();
    }

    async fetchRemovalReasonConfig() {
        try {
            const resp = await axios.get<boolean>('/Configuration/RequireReasonForPatientMacRemoval');
            this.requireRemovalReason = resp.data === true;
        } catch (err) {
            // Default to prompting on a transient failure: the server enforces the rule independently,
            // so skipping the prompt would only produce a confusing 400 the user can't act on.
            console.error("Error loading removal-reason config", err);
            this.requireRemovalReason = true;
        }
    }

    async fetchPrograms() {
        try {
            const resp = await axios.get<Program[]>('/Program');
            this.programs = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    getPatientGSDDDiagnosis() {
        if (!this.patient?.id) return;

        axios.get(`/Diagnosis/Patient/${this.patient.id}`)
            .then(response => {
                this.patientDiagnosis = response.data.sort((a: GSDDPatientDiagnosis, b: GSDDPatientDiagnosis) => {
                    if (!a.created && !b.created) return 0;
                    if (!a.created) return 1;
                    if (!b.created) return -1;
                    return new Date(b.created).getTime() - new Date(a.created).getTime();
                });
            }).catch(error => {
                console.error("Error while loading diagnosis", { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    @Watch('patient.id')
    onPatientChanged(_value: Patient | null, _oldValue: Patient | null) {
        this.clearComponent();
        this.getPatientGSDDDiagnosis();
        // Invalidate the archived cache so a later expand re-fetches for the new patient.
        this.archivedLoaded = false;
        this.archivedDiagnosis = [];
        this.showArchived = false;
    }

    async addDiagnosis() {
        if (!this.selectedDiagnosis || !this.selectedDiagnosis.icD10Code?.trim()) {
            this.$notification(NotificationOptions.error("Please select a Diagnosis."));
            return;
        }

        // Check if diagnosis exists
        const existingDiagnosis = this.patientDiagnosis.find(d => d.icD10Code === this.selectedDiagnosis.icD10Code);
        if (existingDiagnosis) {
            this.$notification(NotificationOptions.error("This Diagnosis has already been added."));
            return;
        }

        // If patient exists, save
        if (this.patient?.id) {
            const data = {
                icD10Code: this.selectedDiagnosis.icD10Code,
                primaryPrograms: this.selectedPrimaryPrograms || []
            }

            try {
                await axios.post(`/Diagnosis/AddToPatient/${this.patient.id}`, data);
                this.getPatientGSDDDiagnosis();
                this.$notification(NotificationOptions.success("Added diagnosis to patient"));
                this.selectedDiagnosis = new ICD10Term();
                this.selectedPrimaryPrograms = [];
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }
        } else {
            // New patient mode
            const newDiagnosis = {
                icD10Code: this.selectedDiagnosis.icD10Code,
                icD10Description: this.selectedDiagnosis.icD10Description || '',
                primaryPrograms: [...(this.selectedPrimaryPrograms || [])],
                created: new Date() as any,
                patientId: 0, // Will be set when patient is saved
                id: 0, // Temporary ID for new diagnosis
                createdBy: '',
                removed: null as any,
                removedBy: ''
            } as GSDDPatientDiagnosis;

            this.patientDiagnosis = [...this.patientDiagnosis, newDiagnosis];
            this.selectedDiagnosis = new ICD10Term();
            this.selectedPrimaryPrograms = [];
        }
    }

    async removeDiagnosis(diagnosis: any) {
        if (this.patient?.id) {
            if (this.requireRemovalReason) {
                const reason = await this.reasonModal.prompt({
                    itemLabel: `${diagnosis.icD10Code} - ${diagnosis.icD10Description ?? ''}`.trim(),
                    required: true,
                    title: 'Remove Condition',
                });
                if (reason === null) return;
                diagnosis.removalReason = reason;
            }

            try {
                await axios.post(`/Diagnosis/RemoveFromPatient`, diagnosis);
                this.getPatientGSDDDiagnosis();
                this.archivedLoaded = false;
                if (this.showArchived) await this.fetchArchivedDiagnosis();
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }
        } else {
            this.patientDiagnosis = this.patientDiagnosis.filter(d => d.icD10Code !== diagnosis.icD10Code);
        }
    }

    async toggleArchived() {
        this.showArchived = !this.showArchived;
        if (this.showArchived && !this.archivedLoaded) await this.fetchArchivedDiagnosis();
    }

    async fetchArchivedDiagnosis() {
        if (!this.patient?.id) return;
        try {
            const resp = await axios.get<GSDDPatientDiagnosis[]>(`/Diagnosis/Patient/${this.patient.id}/Archived`);
            this.archivedDiagnosis = resp.data;
            this.archivedLoaded = true;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    clearComponent() {
        this.patientDiagnosis = new Array<GSDDPatientDiagnosis>();
        this.selectedDiagnosis = new ICD10Term();
        this.selectedPrimaryPrograms = [];
    }

    formatResults(items: ICD10Term[]) {
        return dedupeIcd10Results(items);
    }

    async updateDiagnosis(diagnosis: GSDDPatientDiagnosis) {
        if (this.patient?.id) {
            // Edit mode
            try {
                await axios.post(`/Diagnosis/Update`, diagnosis);
                this.getPatientGSDDDiagnosis();
                this.$notification(NotificationOptions.success("Updated diagnosis on patient"));
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            }
        } else {
            // New patient mode
            this.patientDiagnosis = [...this.patientDiagnosis];
        }
    }

}
</script>

<style scoped lang="scss">
th.remove {
    width: 110px;
}

th.primary-programs {
    width: 350px;
}
</style>
